import React from "react";
import {
  Circle,
  Document,
  Page,
  Path,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";

import {
  IRoiRequestPdfDetailsPage,
  IRoiRequestPdfModel,
  IRoiRequestPdfPage,
  IRoiRequestPdfTablePage,
} from "../model/roi-request-pdf";
import { RoiSelectorColumn } from "../../roi-request-create/model/roi-request-create.types";

const styles = StyleSheet.create({
  page: {
    paddingTop: 66,
    paddingHorizontal: 30,
    paddingBottom: 42,
    fontFamily: "Helvetica",
    color: "#181818",
    fontSize: 9,
    lineHeight: 1.4,
    backgroundColor: "#ffffff",
  },
  header: {
    position: "absolute",
    top: 18,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  brand: {
    flexDirection: "row",
    alignItems: "center",
  },
  brandIcon: {
    marginRight: 8,
  },
  brandText: {
    flexDirection: "column",
  },
  brandTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#12385f",
  },
  brandSubtitle: {
    fontSize: 6.5,
    color: "#1d9fb3",
    letterSpacing: 0.5,
  },
  patientMeta: {
    alignItems: "flex-start",
    minWidth: 190,
  },
  patientName: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    marginBottom: 4,
  },
  patientMetaGrid: {
    flexDirection: "row",
  },
  patientMetaColumn: {
    marginRight: 12,
  },
  patientMetaLabel: {
    fontSize: 5.5,
    color: "#666666",
    marginBottom: 1,
  },
  patientMetaValue: {
    fontSize: 7,
  },
  footerLeft: {
    position: "absolute",
    bottom: 14,
    left: 30,
    color: "#202020",
    fontSize: 8,
  },
  footerRight: {
    position: "absolute",
    bottom: 14,
    right: 30,
    color: "#202020",
    fontSize: 8,
  },
  coverContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  coverTitle: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.45,
    textAlign: "center",
    maxWidth: 340,
  },
  coverSubTitle: {
    marginTop: 12,
    fontSize: 8.5,
    color: "#555555",
    textAlign: "center",
  },
  body: {
    paddingTop: 4,
  },
  tocTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 18,
  },
  tocList: {},
  tocRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginBottom: 12,
  },
  tocLabel: {
    fontSize: 9,
  },
  tocLeader: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#b8b8b8",
    borderBottomStyle: "solid",
    marginHorizontal: 8,
    marginBottom: 2,
  },
  tocPageNumber: {
    width: 18,
    fontSize: 9,
    textAlign: "right",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    textAlign: "center",
    backgroundColor: "#f1f1f1",
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  detailsGroup: {
    marginBottom: 14,
  },
  detailsGroupTitle: {
    fontSize: 8.5,
    fontWeight: 700,
    marginBottom: 8,
  },
  detailRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ececec",
    paddingVertical: 5,
  },
  detailLabel: {
    width: 120,
    fontSize: 7.5,
    paddingRight: 12,
  },
  detailValue: {
    flex: 1,
    fontSize: 7.5,
  },
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d9d9d9",
  },
  headerCell: {
    paddingHorizontal: 4,
    paddingTop: 5,
    paddingBottom: 7,
    fontSize: 6.5,
    fontWeight: 700,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  cell: {
    paddingHorizontal: 4,
    paddingTop: 6,
    paddingBottom: 7,
    fontSize: 6.75,
  },
});

const getColumnFlex = (column: RoiSelectorColumn) => {
  switch (column.key) {
    case "obs_value":
    case "reaction":
    case "document_name":
    case "name":
      return 2.2;
    case "group_name":
    case "obs_name":
    case "document_type":
    case "category":
      return 1.35;
    case "recorded_date":
    case "created_at":
    case "effective_date":
    case "obs_date":
    case "uploaded_by":
    case "source":
      return 1.05;
    default:
      return 1.15;
  }
};

const PdfHeader = ({ data }: { data: IRoiRequestPdfModel }) => (
  <View fixed style={styles.header}>
    <View style={styles.brand}>
      <View style={styles.brandIcon}>
        <Svg width={30} height={22} viewBox="0 0 30 22">
          <Path
            d="M2 15L8 8L12 12L16 6L23 13L28 8"
            stroke="#12385f"
            strokeWidth={2}
            fill="none"
          />
          <Circle cx={12} cy={16} r={5.5} fill="#1d9fb3" />
          <Path
            d="M12 18.5L8.8 15.3C8.1 14.6 8.1 13.5 8.8 12.8C9.4 12.2 10.4 12.1 11.2 12.6L12 13.2L12.8 12.6C13.6 12.1 14.6 12.2 15.2 12.8C15.9 13.5 15.9 14.6 15.2 15.3L12 18.5Z"
            fill="#ffffff"
          />
        </Svg>
      </View>
      <View style={styles.brandText}>
        <Text style={styles.brandTitle}>SouthPark</Text>
        <Text style={styles.brandSubtitle}>HEALTHCARE</Text>
      </View>
    </View>
    <View style={styles.patientMeta}>
      <Text style={styles.patientName}>{data.patient.fullName}</Text>
      <View style={styles.patientMetaGrid}>
        <View style={styles.patientMetaColumn}>
          <Text style={styles.patientMetaLabel}>MRN</Text>
          <Text style={styles.patientMetaValue}>{data.patient.mrn}</Text>
        </View>
        <View style={styles.patientMetaColumn}>
          <Text style={styles.patientMetaLabel}>Date of Birth</Text>
          <Text style={styles.patientMetaValue}>{data.patient.dateOfBirth}</Text>
        </View>
        <View style={styles.patientMetaColumn}>
          <Text style={styles.patientMetaLabel}>Sex</Text>
          <Text style={styles.patientMetaValue}>{data.patient.sex}</Text>
        </View>
      </View>
    </View>
  </View>
);

const PdfFooter = ({ printedAt }: { printedAt: string }) => (
  <Text style={styles.footerLeft}>
    Printed {printedAt}
  </Text>
);

const PdfPageNumber = ({ value }: { value: string }) => (
  <Text style={styles.footerRight}>{value}</Text>
);

const PdfCoverPage = ({ data }: { data: IRoiRequestPdfModel }) => (
  <Page size="A4" style={styles.page}>
    <PdfHeader data={data} />
    <PdfFooter printedAt={data.printedAt} />
    <PdfPageNumber value={`Page 1 of ${data.totalPages}`} />
    <View style={styles.coverContent}>
      <Text style={styles.coverTitle}>{data.title}</Text>
      <Text style={styles.coverSubTitle}>
        Request #{data.fileName.replace("roi-request-", "").replace(".pdf", "")}
      </Text>
    </View>
  </Page>
);

const PdfContentsPage = ({ data }: { data: IRoiRequestPdfModel }) => (
  <Page size="A4" style={styles.page}>
    <PdfHeader data={data} />
    <PdfFooter printedAt={data.printedAt} />
    <PdfPageNumber value={`Page 2 of ${data.totalPages}`} />
    <View style={styles.body}>
      <Text style={styles.tocTitle}>Table of Contents</Text>
      <View style={styles.tocList}>
        {data.tocItems.map((item) => (
          <View key={item.id} style={styles.tocRow}>
            <Text style={styles.tocLabel}>{item.label}</Text>
            <View style={styles.tocLeader} />
            <Text style={styles.tocPageNumber}>{item.pageNumber}</Text>
          </View>
        ))}
      </View>
    </View>
  </Page>
);

const PdfDetailsPage = ({ page }: { page: IRoiRequestPdfDetailsPage }) => (
  <View style={styles.body}>
    <Text style={styles.sectionTitle}>{page.sectionLabel}</Text>
    {page.groups.map((group, groupIndex) => (
      <View
        key={`${page.sectionId}-${page.pageIndexInSection}-${group.title ?? groupIndex}`}
        style={styles.detailsGroup}
      >
        {group.title ? (
          <Text style={styles.detailsGroupTitle}>{group.title}</Text>
        ) : null}
        {group.rows.map((row) => (
          <View
            key={`${page.sectionId}-${page.pageIndexInSection}-${group.title ?? "details"}-${row.label}`}
            style={styles.detailRow}
          >
            <Text style={styles.detailLabel}>{row.label}</Text>
            <Text style={styles.detailValue}>{row.value || "—"}</Text>
          </View>
        ))}
      </View>
    ))}
  </View>
);

const PdfTablePage = ({ page }: { page: IRoiRequestPdfTablePage }) => (
  <View style={styles.body}>
    <Text style={styles.sectionTitle}>{page.sectionLabel}</Text>
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        {page.columns.map((column) => (
          <View
            key={`${page.sectionId}-${column.key}-header`}
            style={{ flex: getColumnFlex(column) }}
          >
            <Text style={styles.headerCell}>{column.header}</Text>
          </View>
        ))}
      </View>
      {page.rows.map((row) => (
        <View
          key={String(row.data_element_id)}
          style={styles.tableRow}
        >
          {page.columns.map((column) => (
            <View
              key={`${String(row.data_element_id)}-${column.key}`}
              style={{ flex: getColumnFlex(column) }}
            >
              <Text style={styles.cell}>{String(row[column.key] ?? "—")}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  </View>
);

const PdfContentPage = ({
  data,
  page,
}: {
  data: IRoiRequestPdfModel;
  page: IRoiRequestPdfPage;
}) => (
  <Page size="A4" style={styles.page}>
    <PdfHeader data={data} />
    <PdfFooter printedAt={data.printedAt} />
    <PdfPageNumber value={`Page ${page.pageNumber} of ${data.totalPages}`} />
    {page.type === "details" ? (
      <PdfDetailsPage page={page} />
    ) : (
      <PdfTablePage page={page} />
    )}
  </Page>
);

export const RoiRequestPdfDocument = ({
  data,
}: {
  data: IRoiRequestPdfModel;
}) => (
  <Document
    title={data.title}
    author="Legacy Link Demo App"
    subject="ROI request export"
    creator="Legacy Link Demo App"
    producer="Legacy Link Demo App"
  >
    <PdfCoverPage data={data} />
    <PdfContentsPage data={data} />
    {data.pages.map((page) => (
      <PdfContentPage
        key={`${page.sectionId}-${page.pageIndexInSection}-${page.pageNumber}`}
        data={data}
        page={page}
      />
    ))}
  </Document>
);
